import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/** One scheduled catalog task, as reported by the catalog's scheduler. */
export interface EntityProviderTask {
  id: string;
  nextRunAt: string | null;
  isRunning: boolean;
  lastRunEndedAt: string | null;
  lastRunError: string | null;
}

export interface EntityProvidersApi {
  listProviders(): Promise<EntityProviderTask[]>;
  triggerProvider(id: string): Promise<void>;
}

export const entityProvidersApiRef = createApiRef<EntityProvidersApi>({
  id: 'plugin.entity-providers.api',
});

export class EntityProvidersClient implements EntityProvidersApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listProviders(): Promise<EntityProviderTask[]> {
    const { providers } = await this.request<{
      providers: EntityProviderTask[];
    }>('/entity-providers');
    return providers ?? [];
  }

  async triggerProvider(id: string): Promise<void> {
    await this.request('/entity-providers/trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
  }

  /**
   * The endpoints hang off the catalog plugin, which is what owns the schedule
   * the entity providers run on. `fetchApi` attaches the caller's Backstage
   * token itself.
   */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('catalog');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...init?.headers },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error?.message ??
          body?.message ??
          `Request failed with status ${response.status}`,
      );
    }

    return response.status === 204 ? (undefined as T) : response.json();
  }
}
